import React, { useEffect, useState } from "react";
import { Table, Button, Modal, Form } from "react-bootstrap";
import API from "../api";
import { useAuth } from "../context/AuthContext";

export default function TeamManager() {
  const { user } = useAuth();
  const [teams, setTeams] = useState([]);
  const [players, setPlayers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);

  const fetchTeams = async () => {
    const res = await API.get(`/teams`);
    setTeams(res.data);
  };

  const fetchPlayers = async () => {
    const res = await API.get(`/players`);
    setPlayers(res.data);
  };

  useEffect(() => {
    if (user?.leagueid) {
      fetchPlayers();
      fetchTeams();
    }
  }, [user?.leagueid]);

  const openModal = (team) => {
    setEditingTeam({ ...team });
    setShowModal(true);
  };

  const handleSave = async () => {
    try {
      await API.put(`/teams/${editingTeam.id}`, editingTeam);
      setShowModal(false);
      fetchTeams();
    } catch (err) {
      alert("Error saving team");
    }
  };

  const usedPlayerIds = new Set(
    teams.flatMap((t) => [t.player1id, t.player2id]).filter(Boolean)
  );

  const availablePlayers = (excludingId = null) =>
    players.filter((p) => {
      return (
        p.active &&
        (!usedPlayerIds.has(p.id) || p.id === excludingId)
      );
    });

  const formatTime = (timeStr) => {
    if (!timeStr) return "";
    const [hour, minute] = timeStr.split(":");
    const date = new Date();
    date.setHours(parseInt(hour));
    date.setMinutes(parseInt(minute));
    return date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true
    });
  };

  return (
    <div className="mt-4">
      <h4>Manage Teams</h4>
      <Table striped bordered hover responsive>
        <thead>
          <tr>
            <th>Team #</th>
            <th>Player 1</th>
            <th>Player 2</th>
            <th>Start Time</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {teams.map((team) => {
            const p1 = players.find((p) => p.id === team.player1id);
            const p2 = players.find((p) => p.id === team.player2id);
            return (
              <tr key={team.id}>
                <td>{team.teamID}</td> {/* Show league-specific team number */}
                <td>{p1 ? `${p1.firstname} ${p1.lastname}` : "-"}</td>
                <td>{p2 ? `${p2.firstname} ${p2.lastname}` : "-"}</td>
                <td>{formatTime(team.starttime)}</td>
                <td>
                  <Button
                    size="sm"
                    onClick={() => openModal(team)}
                    variant="outline-primary"
                  >
                    Edit
                  </Button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>

      {/* Edit Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Team</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editingTeam && (
            <>
              <Form.Group className="mb-3">
                <Form.Label>Player 1</Form.Label>
                <Form.Select
                  value={editingTeam.player1id || ""}
                  onChange={(e) =>
                    setEditingTeam({ ...editingTeam, player1id: parseInt(e.target.value) })
                  }
                >
                  <option value="">-- Select --</option>
                  {availablePlayers(editingTeam.player1id).map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.firstname} {p.lastname}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Player 2</Form.Label>
                <Form.Select
                  value={editingTeam.player2id || ""}
                  onChange={(e) =>
                    setEditingTeam({ ...editingTeam, player2id: parseInt(e.target.value) })
                  }
                >
                  <option value="">-- Select --</option>
                  {availablePlayers(editingTeam.player2id).map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.firstname} {p.lastname}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Start Time</Form.Label>
                <Form.Control
                  type="time"
                  value={editingTeam.starttime || ""}
                  onChange={(e) =>
                    setEditingTeam({ ...editingTeam, starttime: e.target.value })
                  }
                />
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave}>Save</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

